<template>
  <v-card id="order-list">
    <v-card-text class="pb-0">
      <v-row dense>
        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.restaurantIds"
            :items="restaurantDropDown"
            multiple
            chips
            small-chips
            deletable-chips
            dense
            outlined
            hide-details
            clearable
            label="Заведения"
          >
            <template #prepend-item>
              <v-list-item
                ripple
                @mousedown.prevent
                @click="toggleAllRestaurants"
              >
                <v-list-item-action>
                  <v-icon :color="filters.restaurantIds.length ? 'primary' : ''">
                    {{ allRestaurantsIcon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Все заведения</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>

            <template #selection="{ item, index }">
              <v-chip
                v-if="index < 2"
                small
              >
                {{ item.text }}
              </v-chip>
              <span
                v-else-if="index === 2"
                class="text--secondary text-caption align-self-center"
              >
                и ещё {{ filters.restaurantIds.length - 2 }}
              </span>
            </template>
          </v-select>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.paymentMethodIds"
            :items="paymentMethodsDropdown"
            multiple
            chips
            small-chips
            deletable-chips
            dense
            outlined
            hide-details
            clearable
            label="Способы оплаты"
          >
            <template #selection="{ item, index }">
              <v-chip
                v-if="index < 2"
                small
              >
                {{ item.text }}
              </v-chip>
              <span
                v-else-if="index === 2"
                class="text--secondary text-caption align-self-center"
              >
                и ещё {{ filters.paymentMethodIds.length - 2 }}
              </span>
            </template>
          </v-select>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-menu
            v-model="datesOpened"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                :value="datesLabel"
                readonly
                dense
                outlined
                hide-details
                clearable
                label="Период"
                :prepend-inner-icon="icons.mdiCalendarRange"
                v-bind="attrs"
                v-on="on"
                @click:clear="clearDates"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dates"
              range
              locale="ru"
              first-day-of-week="1"
              @change="applyDates"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-text-field
            v-model="filters.searchQuery"
            single-line
            dense
            outlined
            hide-details
            clearable
            placeholder="Поиск..."
            :prepend-inner-icon="icons.mdiMagnify"
          ></v-text-field>
        </v-col>
      </v-row>

      <div class="d-flex align-center flex-wrap mt-4 mb-2">
        <v-btn
          color="secondary"
          outlined
          small
          class="me-3"
          :loading="loading"
          @click="fetchOrders"
        >
          <v-icon
            size="18"
            class="me-1"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          <span>Обновить</span>
        </v-btn>

        <v-tooltip
          v-if="mayExport"
          bottom
        >
          <template #activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
            >
              <v-btn
                color="primary"
                small
                :disabled="!canExport"
                :loading="exporting"
                @click="exportCsv"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiDownloadOutline }}
                </v-icon>
                <span>Выгрузить в CSV</span>
              </v-btn>
            </div>
          </template>
          <span>{{ canExport ? 'Выгрузится всё найденное, без страниц' : 'Заполните заведения, способы оплаты и период' }}</span>
        </v-tooltip>

        <v-spacer></v-spacer>

        <span class="text--secondary text-caption">
          Найдено: {{ totalCount }}
        </span>
      </div>
    </v-card-text>

    <v-data-table
      :headers="headers"
      :items="orders"
      :server-items-length="totalCount"
      :loading="loading"
      :page.sync="filters.pageNumber"
      :items-per-page.sync="filters.pageSize"
      :sort-by.sync="filters.sortBy"
      :sort-desc.sync="filters.sortDesc"
      :item-class="rowClass"
      class="text-no-wrap"
    >
      <template #[`item.id`]="{item}">
        {{ item.id }}
      </template>

      <template #[`item.deliveryOrderId`]="{item}">
        <span v-if="item.deliveryOrderId">{{ item.deliveryOrderId }}</span>
        <v-tooltip
          v-else
          bottom
        >
          <template #activator="{ on, attrs }">
            <span
              class="error--text d-inline-flex align-center"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon
                size="16"
                color="error"
                class="me-1"
              >
                {{ icons.mdiAlertCircleOutline }}
              </v-icon>
              нет
            </span>
          </template>
          <span>Заказ не уехал в Delivery — в кассе его нет. Причина в карточке заказа.</span>
        </v-tooltip>
      </template>

      <template #[`item.createdAt`]="{item}">
        {{ item.orderDate }}
      </template>

      <template #[`item.restaurant`]="{item}">
        {{ item.restaurant }}
      </template>

      <template #[`item.phoneNumber`]="{item}">
        {{ item.phoneNumber }}
      </template>

      <template #[`item.sum`]="{item}">
        {{ item.sum }}
      </template>

      <template #[`item.paymentKind`]="{item}">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-icon
              size="20"
              v-bind="attrs"
              v-on="on"
            >
              {{ paymentIcon(item.paymentKind) }}
            </v-icon>
          </template>
          <span>{{ paymentLabel(item.paymentKind) }}</span>
        </v-tooltip>
      </template>

      <template #[`item.type`]="{item}">
        {{ item.type }}
      </template>

      <template #[`item.source`]="{item}">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-icon
              size="20"
              v-bind="attrs"
              class="rounded-0"
              v-on="on"
            >
              {{ getIcon(item.source) }}
            </v-icon>
          </template>
          <span>{{ item.source === 'MobileApp' ? 'Мобильное приложение' : 'Сайт' }}</span>
        </v-tooltip>
      </template>

      <template #[`item.actions`]="{item}">
        <div class="d-flex align-center justify-center">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                :to="{ name: 'order-form-tab', params: { id: item.id } }"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiEye }}
                </v-icon>
              </v-btn>
            </template>
            <span>Детали</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  mdiTrendingUp,
  mdiCellphone,
  mdiWeb,
  mdiEye,
  mdiDeleteOutline,
  mdiDotsVertical,
  mdiEyeOutline,
  mdiPencilOutline,
  mdiCashMultiple,
  mdiCreditCardOutline,
  mdiQrcode,
  mdiLinkVariant,
  mdiHelpCircleOutline,
  mdiAlertCircleOutline,
  mdiCalendarRange,
  mdiMagnify,
  mdiRefresh,
  mdiDownloadOutline,
  mdiCheckboxBlankOutline,
  mdiMinusBox,
  mdiCloseBox,
} from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'
import { toDateSting, hasPermission } from '@/helpers/index'

const PAYMENT_KINDS = {
  cash: { icon: mdiCashMultiple, label: 'Наличными' },
  card: { icon: mdiCreditCardOutline, label: 'Картой курьеру' },
  qr: { icon: mdiQrcode, label: 'По QR-коду' },
  link: { icon: mdiLinkVariant, label: 'По платёжной ссылке' },
}

export default {
  name: 'OrderList',
  data: () => ({
    icons: {
      mdiCellphone,
      mdiWeb,
      mdiEye,
      mdiTrendingUp,
      mdiPencilOutline,
      mdiDeleteOutline,
      mdiDotsVertical,
      mdiEyeOutline,
      mdiAlertCircleOutline,
      mdiCalendarRange,
      mdiMagnify,
      mdiRefresh,
      mdiDownloadOutline,
      mdiCheckboxBlankOutline,
      mdiMinusBox,
      mdiCloseBox,
    },
    datesOpened: false,
    dates: [],
  }),
  computed: {
    ...mapFormHandlers('order', [
      'filters.pageNumber',
      'filters.pageSize',
      'filters.searchQuery',
      'filters.sortBy',
      'filters.sortDesc',
      'filters.restaurantIds',
      'filters.paymentMethodIds',
      'filters.dateFrom',
      'filters.dateTo',
    ]),
    ...mapState('order', {
      orders: 'list',
      totalCount: 'totalCount',
      loading: 'loading',
      exporting: 'exporting',
    }),
    ...mapGetters('restaurant', { restaurantDropDown: 'restaurantDropDown' }),
    ...mapGetters('dictionary', { paymentMethodsDropdown: 'paymentMethodsDropdown' }),

    mayExport() {
      return hasPermission('Permissions.OrderExport')
    },

    canExport() {
      return this.filters.restaurantIds.length > 0
        && this.filters.paymentMethodIds.length > 0
        && !!this.filters.dateFrom
        && !!this.filters.dateTo
    },
    allRestaurantsSelected() {
      return this.restaurantDropDown.length > 0
        && this.filters.restaurantIds.length === this.restaurantDropDown.length
    },

    allRestaurantsIcon() {
      if (this.allRestaurantsSelected) return this.icons.mdiCloseBox
      if (this.filters.restaurantIds.length) return this.icons.mdiMinusBox

      return this.icons.mdiCheckboxBlankOutline
    },
    datesLabel() {
      const { dateFrom, dateTo } = this.filters

      if (!dateFrom) return ''

      return dateFrom === dateTo ? dateFrom : `${dateFrom} — ${dateTo}`
    },
    headers() {
      return [
        { text: 'Номер', value: 'id' },
        { text: 'Delivery', value: 'deliveryOrderId', sortable: false },
        { text: 'Дата и время', value: 'createdAt' },
        { text: 'Заведение', value: 'restaurant' },
        { text: 'Телефон', value: 'phoneNumber' },
        { text: 'Сумма заказа', value: 'sum' },
        {
          text: 'Оплата', value: 'paymentKind', align: 'center', sortable: false,
        },
        { text: 'Тип', value: 'type', sortable: false },
        { text: 'Источник', value: 'source', sortable: false },
        {
          text: 'Действия',
          value: 'actions',
          align: 'center',
          sortable: false,
        },
      ]
    },
  },
  async created() {
    document.title = 'Заказы'
    this.fetchOrders()
    this.fetchRestaurants()
    this.fetchPaymentMethods()
  },
  methods: {
    ...mapActions('order', { fetchOrders: 'fetchList', exportCsv: 'exportCsv' }),
    ...mapActions('restaurant', { fetchRestaurants: 'fetchDropDown' }),
    ...mapActions('dictionary', { fetchPaymentMethods: 'fetchPaymentMethods' }),
    getIcon(val) {
      switch (val) {
        case 'MobileApp':
          return this.icons.mdiCellphone
        case 'WebSite':
          return this.icons.mdiWeb
        default:
          return this.icons.mdiWeb
      }
    },
    paymentIcon(kind) {
      return (PAYMENT_KINDS[kind] || {}).icon || mdiHelpCircleOutline
    },
    paymentLabel(kind) {
      return (PAYMENT_KINDS[kind] || {}).label || 'Способ оплаты не указан'
    },

    toggleAllRestaurants() {
      this.filters.restaurantIds = this.allRestaurantsSelected
        ? []
        : this.restaurantDropDown.map(x => x.value)
    },

    applyDates(range) {
      const [first, second] = [...range].sort()

      this.filters.dateFrom = first
      this.filters.dateTo = second || first

      if (range.length === 2) this.datesOpened = false
    },
    clearDates() {
      this.dates = []
      this.filters.dateFrom = null
      this.filters.dateTo = null
    },

    rowClass(item) {
      return item.deliveryOrderId ? '' : 'order-row--no-delivery'
    },
    hasNotPermission(val) {
      return !hasPermission(val)
    },
    toDate(val) {
      return toDateSting(val)
    },
  },
}
</script>

<style lang="scss" scoped>
#order-list {
  ::v-deep tbody tr.order-row--no-delivery > td {
    background-color: rgba(255, 76, 81, 0.07);
  }

  ::v-deep tbody tr.order-row--no-delivery:hover > td {
    background-color: rgba(255, 76, 81, 0.12);
  }

  ::v-deep tbody tr.order-row--no-delivery > td:first-child {
    border-left: 3px solid var(--v-error-base);
  }
}
</style>
